// =============================================================================
// US-PLATFORM-03 -- a11y preferences (high-contrast + large-font)
// =============================================================================
//
// Module-level Solid signals for the two accessibility toggles. The active
// account's choices are stored locally per account and reloaded on every
// account switch, preventing one person's display preference from silently
// changing another person's shared-device experience.
//
// The toggles drive `data-a11y-contrast` and `data-a11y-font-size`
// data attributes on the App.tsx root wrapper. CSS in styles.css
// (or component styles) keys off those attributes:
//
//     [data-a11y-contrast="high"] { ... }
//     [data-a11y-font-size="large"] { font-size: 1.25rem; }
//
// Per the spec's AC, the toggles apply system-wide -- every screen
// gets the styling without per-screen opt-in.

import { createSignal } from 'solid-js';
import { appearanceStorageKey, registerAppearanceLoader } from './accountAppearance';

const [highContrastEnabled, setHighContrast] = createSignal(false);
const [largeFontEnabled, setLargeFont] = createSignal(false);

function loadA11yForActiveAccount(): void {
  try {
    const highContrastKey = appearanceStorageKey('high-contrast');
    const largeFontKey = appearanceStorageKey('large-font');
    setHighContrast(highContrastKey !== null && localStorage.getItem(highContrastKey) === 'true');
    setLargeFont(largeFontKey !== null && localStorage.getItem(largeFontKey) === 'true');
  } catch {
    setHighContrast(false);
    setLargeFont(false);
  }
}

registerAppearanceLoader(loadA11yForActiveAccount);

export function setHighContrastEnabled(value: boolean): void {
  setHighContrast(value);
  try {
    const key = appearanceStorageKey('high-contrast');
    if (key !== null) localStorage.setItem(key, String(value));
  } catch {
    /* local storage is unavailable; retain the current session preference. */
  }
}

export function setLargeFontEnabled(value: boolean): void {
  setLargeFont(value);
  try {
    const key = appearanceStorageKey('large-font');
    if (key !== null) localStorage.setItem(key, String(value));
  } catch {
    /* local storage is unavailable; retain the current session preference. */
  }
}

export { highContrastEnabled, largeFontEnabled };
